import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import dayjs from "util/dayjs-init.js";
import "dayjs/locale/ja";
import CalendarIcon from "../../../assets/images/cargo/chart/calendar.svg";
import { Grid, Button, IconButton, Popover, Select, MenuItem } from "@mui/material";
import makeStyles from '@mui/styles/makeStyles';
import { COLORS } from "../../constants/colors.js";
import { useSelector } from "react-redux";
import { getShorthandSettings } from "../../model/slice/dateTimeShorthandSlice.js";
import { selectedTimezone } from "../../model/slice/timezoneSlice.js";
import { TIMEZONE } from "../../constants/timezone.js";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { CalendarIcon as MuiCalendarIcon, ClockIcon as MuiClockIcon, LocalizationProvider, StaticDateTimePicker } from "@mui/x-date-pickers";
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';
import { Box } from "@mui/system";

const useStyles = makeStyles(() => ({
  timePickerContainer: {
    display: "flex",
    background: COLORS.componentBackColor,
    justifyContent: "flex-end",
    margin: "10px 0",
    right: "0%",
  },
  popoverContentContainer: {
    background: COLORS.componentBackColor,
    color: COLORS.white,
    zIndex: 500,
    borderRadius: "10px",
    border: "solid 1px white",
  },
  submitButton: {
    width: "80%",
    height: "34px",
    borderRadius: "5px",
    background: "#19b2ab",
    textTransform: "none",
    fontSize: "12px",
    color: "#21262f",
    "& span": {
      marginLeft: "0px",
    },
    "&:hover": {
      background: "#19b2ab",
    },
  },
  advancedSettings: {
    fontSize: "12px",
    width: "70%",
    display: "inline-flex",
    alignItems: "center",
    color: 'white'
  },
  shorthandSettingsContainer: {
    marginLeft: "15px",
    marginBottom: "10px",
  },
  clockIconHolder: {
    position: "relative",
    top: "2px",
  },
  smallIncrement: {
    display: "flex",
    alignItems: "center",
  },
  largeIncrement: {
    display: "flex",
    alignItems: "center",
  },
}));

export const CargoDateTimePicker = (props) => {
  const {
    date,
    submitDateTimeChange,
    disabled = false,
    isMaximized = false,
    latestDateTime,
  } = props;
  const styles = useStyles();
  const timezone = useSelector(selectedTimezone);
  const isUTC = timezone === TIMEZONE.UTC;
  const currentShorthandSettings = useSelector(getShorthandSettings);
  const [maxDateTime, setMaxDateTime] = useState(dayjs());
  // for additional settings
  const [shorthandSettingsForShort, setShorthandSettingsForShort] = useState(
    currentShorthandSettings.short
  );
  const [shorthandSettingsForLong, setShorthandSettingsForLong] = useState(
    currentShorthandSettings.long
  );
  const [currentSelected, setCurrentSelected] = useState(date ? dayjs(date) : dayjs());

  const shortOptions = [
    { value: 1, label: "1min" },
    { value: 2, label: "2min" },
    { value: 3, label: "3min" },
    { value: 5, label: "5min" },
    { value: 7, label: "7min" },
    { value: 10, label: "10min" },
  ];

  const longOptions = [
    { value: 10, label: "10min" },
    { value: 15, label: "15min" },
    { value: 20, label: "20min" },
    { value: 25, label: "25min" },
    { value: 30, label: "30min" },
  ];

  const timeShorthandHandler = (e) => {
    if (e.target.value < 10) {
      setShorthandSettingsForShort(e.target.value);
    } else if (e.target.value === 10 && shorthandSettingsForLong === 10) {
      setShorthandSettingsForShort(e.target.value);
    } else if (e.target.value === 10 && shorthandSettingsForShort === 10) {
      setShorthandSettingsForLong(e.target.value);
    } else {
      setShorthandSettingsForLong(e.target.value);
    }
  };

  useEffect(() => {
    if (latestDateTime) {
      setMaxDateTime(timezone === TIMEZONE.UTC ? dayjs.utc(latestDateTime) : dayjs(latestDateTime));
    }
  }, [latestDateTime]);

  const [anchorEl, setAnchorEl] = useState(null);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleAccept = () => {
    submitDateTimeChange(isUTC ? dayjs.utc(currentSelected) : dayjs(currentSelected), shorthandSettingsForShort, shorthandSettingsForLong);
    handleClose();
  }

  const handleCancel = () => {
    setCurrentSelected(null);
    setShorthandSettingsForShort(
      currentShorthandSettings.short
    );
    setShorthandSettingsForLong(
      currentShorthandSettings.long
    );
    handleClose()
  }

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const CustomCargoActionBar = () => {
    return (
      <Box sx={{gridColumn: 2, gridRow: 3, display: 'flex', justifyContent: 'end', position: 'relative', bottom: '12px', right: '12px'}}>
        <Button onClick={handleCancel} sx={{backgroundColor: '', color: '#28B6C7'}}>Cancel</Button>
        <Button onClick={handleAccept} sx={{backgroundColor: '#28B6C7', color: 'white'}}>OK</Button>
      </Box>
    )
  }
  return (
    <>
      <IconButton onClick={handleClick}>
        <img src={CalendarIcon} width={20} height={20} style={{marginTop: '10px'}} />
      </IconButton>
      <Popover
      id={id}
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'center',
        horizontal: 'right',
      }}>
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <StaticDateTimePicker 
        value={currentSelected} 
        onAccept={handleAccept}
        onChange={setCurrentSelected}
        timezone={isUTC ? TIMEZONE.UTC : "default"} 
        maxDateTime={maxDateTime}
        slots={{actionBar: CustomCargoActionBar}}
        sx={{
          '& input': {color: 'white', cursor: 'pointer', padding: '4px 12px'}, 
          '& input::selection' : {backgroundColor: 'transparent'},
          background: '#2f3a47', 
          width: '100%',
        }}
        slotProps={{
          mobilePaper: {sx : {background: '#2f3a47', color: 'white'}}, 
          leftArrowIcon: {sx: {color: 'white'}},
          rightArrowIcon: {sx: {color: 'white'}},
          tabs: {
            dateIcon: <MuiCalendarIcon sx={{fill: "white"}} />,
            timeIcon: <MuiClockIcon sx={{fill: 'white'}} />,
            sx: {'.MuiTabs-indicator': {backgroundColor: '#28B6C7'}}
          },
          toolbar: {
            toolbarFormat: 'MM月DD日',
            sx: {'& span': {color: 'white'}, '& span.Mui-selected': {color: 'white'}}
          },
          mobileTransition: {
            sx: {'&.MuiDayCalendar-weekDayLabel': {color: 'white'}}
          },
          calendarHeader: {
            format: "YYYY年 MM月",
            sx:{'& span' : {color: 'white'}, '& svg': {fill: 'white'}, '&.MuiPickersCalendarHeader-root': {color: 'white'}, 'MuiPickersDay-root': {color: 'white'}}
          },
          day: {
            sx: {
              color: 'white', 
              '&.MuiPickersDay-today:not(.Mui-selected)': {border: '1px solid rgba(255, 255, 255, 0.6)'}, 
              '&.MuiPickersDay-root.Mui-selected': {backgroundColor: '#28B6C7'},
            },
          },
          layout: {
            sx: {
                  '& .MuiDayCalendar-weekDayLabel': {
                    color: 'white',
                  },
                  '& .MuiClockNumber-root': {
                    color: 'white'
                  },
                  '& .MuiClock-pin': {
                    backgroundColor: '#28B6C7'
                  },
                  '& .MuiClockPointer-root': {
                    backgroundColor: '#28B6C7'
                  },
                  '& .MuiClockPointer-thumb': {
                    backgroundColor: '#28B6C7',
                    border: '16px solid #28B6C7'
                  }
            }
          }
        }}
        />
        <Grid container sx={{backgroundColor: '#2f3a47'}}>
          <Grid item xs={12}>
            <div className={styles.smallIncrement}>
              <div className={styles.advancedSettings}>
                <ChevronLeftIcon />
                <span style={{ marginRight: "20px" }}>Set small Increment</span>
              </div>
              <Select
                value={shortOptions.find(
                  (option) => option.value === shorthandSettingsForShort
                ).value}
                options={shortOptions}
                onChange={timeShorthandHandler}
                name="shorthandOption"
                MenuProps={{sx: {'.MuiMenu-paper': {backgroundColor: '#2f3a47'}}}}
                sx={{color: "white", "& Fieldset": {borderWidth: '0px'}, "& Fieldset :focus": {borderWidth: '0px'}, ".MuiSelect-iconOutlined": {color: '#28B6C7'}}}
              >
                              {
                shortOptions.map((e) => <MenuItem key={e.value} value={e.value} sx={{ color: 'white', ':hover': {backgroundColor: 'rgb(38, 48, 59)'}, ':selected': {backgroundColor: ' rgb(33, 38, 47)'}}}>{e.label}</MenuItem>)
              }
              </Select>
            </div>
            <div className={styles.largeIncrement}>
              <div className={styles.advancedSettings}>
                <KeyboardDoubleArrowLeftIcon/>
                <span style={{ marginRight: "20px" }}>Set large Increment</span>
              </div>
              <Select
                value={longOptions.find(
                  (option) => option.value === shorthandSettingsForLong
                ).value}
                onChange={timeShorthandHandler}
                name="shorthandOption"
                MenuProps={{sx: {'.MuiMenu-paper': {backgroundColor: '#2f3a47'}}}}
                sx={{
                  color: "white", 
                  "& Fieldset": {borderWidth: '0px'}, 
                  "& Fieldset :focus": {borderWidth: '0px'}, 
                  ".MuiSelect-iconOutlined": {color: '#28B6C7'},
                  }}
              >
              {
                longOptions.map((e) => 
                  <MenuItem key={e.value} value={e.value} sx={{ color: 'white', ':hover': {backgroundColor: 'rgb(38, 48, 59)'}, ':selected': {backgroundColor: ' rgb(33, 38, 47)'}}}>
                    {e.label}
                  </MenuItem>)
              }
              </Select>
            </div>
          </Grid>
        </Grid>
    </LocalizationProvider>
      </Popover>
    </>
  );
};

CargoDateTimePicker.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
  submitDateTimeChange: PropTypes.func,
  disabled: PropTypes.bool,
  isNoData: PropTypes.bool,
  isMaximized: PropTypes.bool,
  latestDateTime: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date), PropTypes.object]),
};
